/**
 * Who is responsible for a row — `KLQ-20260828-003`, resolved.
 *
 * **The whole resolution is client-side, and that is the design rather than a shortcut.** The
 * server stores one nullable column per tree and hands it over raw; the client already holds
 * the entire hierarchy (`GET /hierarchy` is the flat tree) and the entire composition (`GET
 * /objects` is unpaginated by its own contract, because a composition is a table of contents).
 * Walking ancestors over data that is already in memory makes §D.1's *"resolved at read time,
 * never materialised"* literally true: no per-row server computation, no resolved table to
 * keep in step, and no assignee row written by anybody editing a floor.
 *
 * It also settles the scope question for free. The tree arrives already pruned by
 * `hierarchy.scope.ScopeView`, and the server strips `responsible_user_id` from any node the
 * caller may only *see* rather than reach. So a member scoped to Floor 3 cannot resolve to
 * Building B's architect, because that value never reaches this file — the leak is prevented
 * by absence rather than by a rule this module would have to remember.
 *
 * **Three functions, one rule.** Nearest ancestor with a value wins, and the source is carried
 * back with the answer because §E requires an inherited name to say where it came from. A
 * person must never have to guess whether a name on a row was chosen for that row.
 */

import type { ProjectObject } from "@/features/projects";

import type { SlotNode, TreeNode } from "./tree";

export interface ResolvedResponsible {
  readonly userId: string;
  /**
   * Where the value came from, or `null` when the row carries it itself.
   *
   * The name and not just the id: the one thing an inherited value owes the reader is *"from
   * Floor 3"*, and a component that had to look the ancestor up again would be a second walk
   * over the tree this module just walked.
   */
  readonly from: { readonly id: string; readonly name: string } | null;
}

/**
 * Every container's answer, in one pass down the tree.
 *
 * Carrying the inherited value down as the walk descends rather than walking up from each row:
 * the same answer, one traversal instead of one per node, and the nearest-ancestor rule falls
 * out of the recursion instead of being implemented by it.
 *
 * A slot is not in this map — it has no column of its own. `forSlot` reads its parent's entry.
 */
export function responsibleByNode(
  roots: readonly TreeNode[],
): ReadonlyMap<string, ResolvedResponsible> {
  const out = new Map<string, ResolvedResponsible>();
  const walk = (
    nodes: readonly TreeNode[],
    inherited: ResolvedResponsible | null,
  ): void => {
    for (const node of nodes) {
      if (node.kind !== "container") continue;
      // A synthetic pivot heading has no row in the database and therefore no value of its
      // own; it passes what it was given straight through rather than interrupting the chain.
      const own = node.synthetic ? null : node.responsibleUserId;
      const here: ResolvedResponsible | null =
        own === null
          ? inherited
          : { userId: own, from: null };
      if (here !== null) {
        out.set(node.id, here);
      }
      walk(
        node.children,
        // What the children inherit: this node, named — including when this node was itself
        // inheriting, because the source a person wants is the level that actually carries the
        // value, not the one immediately above them.
        here === null
          ? null
          : here.from === null
            ? { userId: here.userId, from: { id: node.id, name: node.name } }
            : here,
      );
    }
  };
  walk(roots, null);
  return out;
}

/**
 * The same rule up the object chain — `parent_id`, at most `OBJECT_DEPTH_LIMIT` rows deep.
 *
 * A list rather than a tree because that is the shape the endpoint returns, and because the
 * chain is short enough that a map lookup per step beats building a second tree to walk.
 */
export function responsibleByObject(
  objects: readonly ProjectObject[],
): ReadonlyMap<string, ResolvedResponsible> {
  const rows = new Map(objects.map((row) => [row.id, row]));
  const out = new Map<string, ResolvedResponsible>();

  for (const row of objects) {
    let current: ProjectObject | undefined = row;
    let from: { id: string; name: string } | null = null;
    // Bounded by the map itself: every step moves to a strictly different row and stops at the
    // first one already answered, so a cycle the database cannot contain cannot hang this.
    const seen = new Set<string>();
    while (current !== undefined && !seen.has(current.id)) {
      seen.add(current.id);
      const owner = current.responsible_user_id ?? null;
      if (owner !== null) {
        out.set(row.id, { userId: owner, from });
        break;
      }
      const parentId: string | null = current.parent_id ?? null;
      if (parentId === null) break;
      const parent: ProjectObject | undefined = rows.get(parentId);
      if (parent === undefined) break;
      from = { id: parent.id, name: parent.label };
      current = parent;
    }
  }
  return out;
}

/**
 * A drawing sits in both trees at once, so §C.3 orders them: its own value (it has none), then
 * the nearest **spatial** ancestor, then the nearest **object** ancestor.
 *
 * **The tree beats the axis** because the tree is where the work sits and the object is the
 * label the drawing is filed under. Letting the label outrank the place would mean an architect
 * who owns Building B could be silently overridden on one of its drawings by whoever was named
 * on SO 701.
 *
 * The *Zoznam dokumentácie* does not use this function: it names the projektant of a part from
 * the object's own column, directly (§C.4). Naming a person because of where a drawing happened
 * to sit in somebody's floor plan would be wrong legally rather than cosmetically.
 */
export function responsibleForSlot(
  slot: SlotNode,
  parentNodeId: string | null,
  byNode: ReadonlyMap<string, ResolvedResponsible>,
  byObject: ReadonlyMap<string, ResolvedResponsible>,
): ResolvedResponsible | null {
  const spatial = parentNodeId === null ? undefined : byNode.get(parentNodeId);
  if (spatial !== undefined) return spatial;
  const object = slot.objectId === null ? undefined : byObject.get(slot.objectId);
  return object ?? null;
}
